import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { LENS_META, LENS_WIKI, AXES_BY_LENS, AXIS_WIKI, OVERVIEW, ROLES } from './Pages/Sos2/wikiContent';
import { AXIS_INFO } from './Pages/Sos2/axisInfo';
import { WikiBlock, inlineMarkup } from './Pages/Sos2/InfoUI';

// lrGuideApp — the public Layered Read guide on /how-layered-read-works (2026-10-22).
// Renders the SAME articles the in-app wiki drawer shows (wikiContent.js / axisInfo.js), inline,
// so the guide can never drift from the product. Only the plain one-line questions and the layer
// one-liners below are local: they are the explainer video's wording (video/layered-read/src/
// Scene6Measures.jsx, Codex-reviewed rounds 5–6), used as headlines over the full articles.
//
// Mount: <div id="lr-guide"></div> + <script src="{{ mix('js/lrGuideApp.js') }}"></script>
// Deep links: /how-layered-read-works#axis-pressure opens that axis; #group-design scrolls.
// Words (owner, 2026-10-22, docs/design/lr-terminology-v1.md): axes, groups, levels.

const QUESTION = {
  contract: 'Is it clear what the scene is about?',
  pressure: 'Is something actually pushing the scene?',
  development: 'Does it go deeper, or repeat itself?',
  movement: 'Is something different by the end?',
  consequence: 'Does what happens here matter later?',
  economy: 'Is every moment needed?',
  assembly: 'Do the moments build on each other?',
  clarity: 'Can you follow what matters?',
  freshness: 'Is it a fresh take, or one we’ve seen?',
  unpredictability: 'Does it surprise you, in a way that’s earned?',
  charge: 'Do you feel something?',
  grip: 'Does it make you want the next scene?',
};
const LAYERS = [
  { key: 'Design', wiki: 'design', plain: 'The blueprint: what the scene is built to do.' },
  { key: 'Execution', wiki: 'execution', plain: 'The build: how it’s put on the page.' },
  { key: 'Involvement', wiki: 'read', plain: 'The experience: what it’s like to watch.' },
];

const CSS = `
.lrg{--ink:#142f3d;--copy:#405967;--line:rgba(8,58,84,.14);color:var(--ink)}
.lrg h2{margin:0 0 8px;font-size:28px;letter-spacing:-.02em;color:#083A54}
.lrg-sec{padding:36px 0 8px;scroll-margin-top:80px}
.lrg-lead{margin:0 0 20px;color:var(--copy);font-size:17px;line-height:1.6;max-width:720px}
.lrg-layers{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}
.lrg-layer{border:1px solid var(--line);border-top:5px solid var(--c);border-radius:12px;padding:16px 18px;background:#fff;scroll-margin-top:80px}
.lrg-layer h3{margin:0 0 4px;font-size:21px;color:var(--c)}
.lrg-layer .lrg-plain{margin:0 0 10px;font-weight:600;color:var(--ink)}
.lrg-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.lrg-chip{border:1px solid var(--c);color:var(--c);background:var(--t);border-radius:999px;padding:2px 10px;font:700 13px/1.6 inherit;text-decoration:none!important}
.lrg-group{margin:22px 0 8px;font-size:18px;color:var(--c)}
.lrg-m{border:1px solid var(--line);border-left:4px solid var(--c);border-radius:10px;margin:0 0 10px;background:#fff;scroll-margin-top:80px}
.lrg-m>button{all:unset;box-sizing:border-box;display:flex;gap:14px;align-items:baseline;width:100%;padding:14px 16px;cursor:pointer}
.lrg-m>button:focus-visible{outline:2px solid #083A54;border-radius:10px}
.lrg-m-name{flex:none;min-width:150px;font-weight:800;font-size:17px;color:var(--c)}
.lrg-m-q{flex:1;font-size:17px;color:var(--ink)}
.lrg-m-tog{flex:none;color:#083A54;font-weight:700;font-size:14px}
.lrg-m-body{padding:0 18px 8px 18px;border-top:1px solid var(--line)}
.lrg-m-short{margin:12px 0 10px;color:var(--copy);font-size:15px;font-style:italic}
.lrg-clip{display:block;width:100%;max-width:760px;height:auto;aspect-ratio:16/9;margin:14px 0 4px;border-radius:10px;background:#0b2a3b}
.lrg-box{border:1px solid var(--line);border-radius:12px;padding:16px 20px;background:#fff;max-width:760px}
.lrg-roles{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.lrg-role{border:1px solid var(--line);border-radius:10px;padding:12px 14px;background:#fff}
.lrg-role b{display:block;color:#083A54;margin-bottom:2px}
.lrg-role span{color:var(--copy);font-size:15px;line-height:1.5}
@media (max-width:820px){.lrg-layers,.lrg-roles{grid-template-columns:1fr}.lrg-m>button{flex-wrap:wrap;gap:4px 12px}.lrg-m-name{min-width:0}}
`;

// The short narrated clip for an axis (video/layered-read AxisClip; files in public/video/layered-read/axes/).
function ClipVideo({ name }) {
  const base = `/video/layered-read/axes/${name}`;
  return (
    <video className="lrg-clip" data-lr-track={name} controls playsInline preload="none" poster={`${base}.jpg`} width="1920" height="1080">
      <source src={`${base}.mp4`} type="video/mp4" />
      <track kind="captions" srcLang="en" label="English" src={`${base}.vtt`} default />
    </video>
  );
}

function Measure({ id, open, onToggle }) {
  const info = AXIS_INFO[id], w = AXIS_WIKI[id] || {}, m = LENS_META[info.lens];
  return (
    <div className="lrg-m" id={`axis-${id}`} style={{ '--c': m.color }}>
      <button type="button" aria-expanded={open} onClick={() => onToggle(id)}>
        <span className="lrg-m-name">{info.label}</span>
        <span className="lrg-m-q">{QUESTION[id]}</span>
        <span className="lrg-m-tog">{open ? 'Close' : 'Read more'}</span>
      </button>
      {open ? (
        <div className="lrg-m-body">
          <ClipVideo name={`axis-${id}`} />
          {w.short ? <p className="lrg-m-short">{inlineMarkup(w.short)}</p> : null}
          {(w.body || [info.measures]).map((b, i) => <WikiBlock key={i} block={b} accent={m.color} />)}
        </div>
      ) : null}
    </div>
  );
}

function Guide() {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  useEffect(() => {
    const go = () => {
      const h = decodeURIComponent(location.hash.slice(1));
      const m = h.match(/^(?:axis|measure)-(\w+)$/);   // #axis-pressure (old links: #measure-pressure)
      if (m && AXIS_INFO[m[1]]) {
        const id = m[1];
        setOpen((s) => new Set(s).add(id)); setTimeout(() => document.getElementById(`axis-${id}`)?.scrollIntoView({ block: 'start' }), 30);
      }
    };
    go(); window.addEventListener('hashchange', go);
    return () => window.removeEventListener('hashchange', go);
  }, []);

  return (
    <div className="lrg">
      <section className="lrg-sec" id="groups">
        <h2>Three groups</h2>
        <p className="lrg-lead">A scene is planned, built, and experienced. Each group has its own axes, and a problem in one group usually needs a different kind of rewrite from a problem in another.</p>
        <div className="lrg-layers">
          {LAYERS.map((L) => {
            const m = LENS_META[L.key], w = LENS_WIKI[L.wiki];
            return (
              <div key={L.key} className="lrg-layer" id={`group-${L.wiki === 'read' ? 'involvement' : L.wiki}`} style={{ '--c': m.color, '--t': m.tint }}>
                <h3>{L.key}</h3>
                <p className="lrg-plain">{L.plain}</p>
                <ClipVideo name={`group-${L.wiki === 'read' ? 'involvement' : L.wiki}`} />
                {w.body.map((b, i) => <WikiBlock key={i} block={b} accent={m.color} />)}
                <div className="lrg-chips">
                  {AXES_BY_LENS[L.key].map((id) => (
                    <a key={id} className="lrg-chip" href={`#axis-${id}`}>{AXIS_INFO[id].label}</a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="lrg-sec" id="axes">
        <h2>Twelve axes</h2>
        <p className="lrg-lead">Each axis asks one question about a scene, and each is scored separately. Open one for a short video and the full guide: what it looks for, why it matters, what readers feel when it’s missing, where the idea comes from, and how it usually gets fixed.</p>
        <ClipVideo name="axis-intro" />
        {LAYERS.map((L) => (
          <div key={L.key} style={{ '--c': LENS_META[L.key].color }}>
            <h3 className="lrg-group">{L.key}</h3>
            {AXES_BY_LENS[L.key].map((id) => <Measure key={id} id={id} open={open.has(id)} onToggle={toggle} />)}
          </div>
        ))}
      </section>

      <section className="lrg-sec" id="colours">
        <h2>What the colours mean</h2>
        <p className="lrg-lead">The map opens coloured by Triage: how much work each part needs. You can recolour it by one group, or by a single axis.</p>
        <div className="lrg-box">
          {OVERVIEW.triage.body.map((b, i) => <WikiBlock key={i} block={b} accent="#14536e" />)}
        </div>
      </section>

      <section className="lrg-sec" id="scene-types">
        <h2>Scene types</h2>
        <p className="lrg-lead">Every scene also gets a type. The type decides what the scene owes: a quiet setup scene isn’t expected to turn the story, so a low score on Movement there isn’t a fault.</p>
        <div className="lrg-roles">
          {Object.entries(ROLES).map(([id, r]) => (
            <div key={id} className="lrg-role"><b>{r.label}</b><span>{inlineMarkup(r.short)}</span></div>
          ))}
        </div>
      </section>
    </div>
  );
}

function mount() {
  const el = document.getElementById('lr-guide');
  if (!el || el.dataset.mounted) return;
  el.dataset.mounted = '1';
  if (!document.getElementById('lrg-styles')) {
    const s = document.createElement('style'); s.id = 'lrg-styles'; s.textContent = CSS; document.head.appendChild(s);
  }
  createRoot(el).render(<Guide />);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
